import {
  RefObject,
  ChangeEvent,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { reduce, has, filter, isArray, includes, isEqual } from 'lodash';

import { Nullable } from '~globals/types/commons';
import { VehicleBase } from '~services/vehicle/types';
import { DriverCompanyBase } from '~services/driver/types';
import { TemplateRouteFilter } from '~services/templateRoute/types';
import { useParseLocationQuery } from '~hooks/useParseLocationQuery';
import { FiltersValuesReturn, useFiltersValues } from '~hooks/useFiltersValues';
import { CustomAutocompleteRef } from '~components/CustomAutocomplete/types';
import { getItemValue } from '~utils/helpers';

interface TemplatesListQueryParamsFilters {
  templateCode?: string;
  vehicle?: string;
  driver?: string;
}

export interface TemplatesListFilters {
  templateCode: string;
  vehicle: Nullable<VehicleBase>;
  driver: Nullable<DriverCompanyBase>;
}

interface TemplatesListSelectData<T> {
  autocompleteRef: RefObject<CustomAutocompleteRef<T, false>>;
  loading: boolean;
  list: T[];
}

interface TemplatesListSelects {
  vehicles: TemplatesListSelectData<VehicleBase>;
  drivers: TemplatesListSelectData<DriverCompanyBase>;
}

interface TemplatesListParams {
  selectsData: TemplatesListSelects;
}

interface TemplatesListReturn
  extends FiltersValuesReturn<TemplatesListFilters> {
  parseFiltersRequest: () => TemplateRouteFilter;
}

type TemplatesListFiltersAppliedType = 'queryParams' | 'filters';

type TemplatesListFiltersApplied = Record<
  keyof TemplatesListFilters,
  TemplatesListFiltersAppliedType
>;

export const initialFilters: TemplatesListFilters = {
  templateCode: '',
  vehicle: null,
  driver: null,
};

export const useTemplatesListFilters = ({
  selectsData,
}: TemplatesListParams): TemplatesListReturn => {
  const queryFilters = useParseLocationQuery<TemplatesListQueryParamsFilters>();

  const [filtersApplied, setFilterApplied] =
    useState<TemplatesListFiltersApplied>(() => {
      const builder = reduce(
        initialFilters,
        (acc, _, key) => {
          const keyTyped = key as keyof TemplatesListFilters;

          acc[keyTyped] = has(queryFilters, keyTyped)
            ? 'queryParams'
            : 'filters';

          return acc;
        },
        {} as TemplatesListFiltersApplied,
      );

      return builder;
    });

  const getFilterAppliedByKey = useCallback(
    <Key extends keyof TemplatesListFiltersApplied>(
      key: Key,
    ): TemplatesListFiltersApplied[Key] => getItemValue(filtersApplied, key),
    [filtersApplied],
  );

  const { filters, setFilterByKey, clearFilters } =
    useFiltersValues(initialFilters);

  const setFilterAppliedByKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (key: string, value: any) => {
      setFilterByKey(key, value);

      const keyTyped = key as keyof TemplatesListFilters;

      setFilterApplied((prevFiltersApplied) => ({
        ...prevFiltersApplied,
        [keyTyped]: 'filters',
      }));
    },
    [setFilterByKey],
  );

  const handleChangeInputFilterApplied = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = event.target ?? event.currentTarget;

      setFilterAppliedByKey(name, value);
    },
    [setFilterAppliedByKey],
  );

  const clearFiltersApplied = useCallback(() => {
    clearFilters();

    setFilterApplied({
      templateCode: 'filters',
      vehicle: 'filters',
      driver: 'filters',
    });
  }, [clearFilters, setFilterApplied]);

  const getValueQueryParams = useCallback(
    <Key extends keyof TemplatesListQueryParamsFilters>(
      key: Key,
    ): TemplatesListQueryParamsFilters[Key] => getItemValue(queryFilters, key),
    [queryFilters],
  );

  const getValueCurrentFilters = useCallback(
    <Key extends keyof TemplatesListFilters>(
      key: Key,
    ): TemplatesListFilters[Key] => getItemValue(filters, key),
    [filters],
  );

  const setValueFilterByQueryParams = useCallback(
    ({
      key,
      transformValue,
    }: {
      key: keyof TemplatesListQueryParamsFilters;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      transformValue?: (currentValue: any) => void;
    }) => {
      const currentValueByQueryParam = getValueQueryParams(key);

      if (
        currentValueByQueryParam !== undefined &&
        currentValueByQueryParam !== null
      ) {
        const value = transformValue
          ? transformValue(currentValueByQueryParam)
          : currentValueByQueryParam;

        setFilterByKey(key, value);
      }
    },
    [getValueQueryParams, setFilterByKey],
  );

  const getOptionValueByExtractorKey = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      currentOption,
      extractorKey,
    }: {
      currentOption: T;
      extractorKey: string;
    }): string =>
      typeof currentOption !== 'string'
        ? currentOption[extractorKey]
        : currentOption,
    [],
  );

  const getFilteredSelectDataByQueryParam = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      currentSelectData,
      currentValueQueryParam,
      extractorKey,
    }: {
      currentSelectData: TemplatesListSelectData<T>;
      currentValueQueryParam: string | string[];
      extractorKey: string;
    }): T[] => {
      const filteredData = filter(currentSelectData.list, (currentOption) => {
        const currentOptionId = getOptionValueByExtractorKey({
          currentOption,
          extractorKey,
        });

        if (isArray(currentValueQueryParam)) {
          return includes(currentValueQueryParam, currentOptionId);
        }

        return isEqual(currentValueQueryParam, currentOptionId);
      });

      return filteredData;
    },
    [getOptionValueByExtractorKey],
  );

  const setValueFilterWithSelectDataByQueryParams = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T extends Record<string, any>>({
      key,
      currentSelectData,
      extractorKey,
    }: {
      key: keyof TemplatesListQueryParamsFilters;
      currentSelectData: TemplatesListSelectData<T>;
      extractorKey: string;
    }) => {
      const currentValueByQueryParam = getValueQueryParams(key);

      if (
        currentValueByQueryParam !== undefined &&
        currentValueByQueryParam !== null &&
        !currentSelectData.loading
      ) {
        const filteredOptions = getFilteredSelectDataByQueryParam({
          currentSelectData,
          currentValueQueryParam: currentValueByQueryParam,
          extractorKey,
        });

        if (filteredOptions.length > 0) {
          const currentValue = filteredOptions[0];

          setFilterByKey(key, currentValue);
          currentSelectData.autocompleteRef.current?.setValue(currentValue);
        }
      }
    },
    [getValueQueryParams, getFilteredSelectDataByQueryParam, setFilterByKey],
  );

  useEffect(() => {
    setValueFilterByQueryParams({ key: 'templateCode' });
  }, [setValueFilterByQueryParams]);

  useEffect(() => {
    setValueFilterWithSelectDataByQueryParams({
      key: 'vehicle',
      currentSelectData: selectsData.vehicles,
      extractorKey: 'id',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectsData.vehicles.list,
    selectsData.vehicles.loading,
    setValueFilterWithSelectDataByQueryParams,
  ]);

  useEffect(() => {
    setValueFilterWithSelectDataByQueryParams({
      key: 'driver',
      currentSelectData: selectsData.drivers,
      extractorKey: 'id',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectsData.drivers.list,
    selectsData.drivers.loading,
    setValueFilterWithSelectDataByQueryParams,
  ]);

  const parseTemplateCodeForRequest = useCallback((): Nullable<string> => {
    const currentTypeTemplateCodeFilterApplied =
      getFilterAppliedByKey('templateCode');

    const currentTemplateCodeQueryParam = getValueQueryParams('templateCode');
    const currentTemplateCodeFilter = getValueCurrentFilters('templateCode');

    const templateCode =
      currentTypeTemplateCodeFilterApplied === 'queryParams'
        ? currentTemplateCodeQueryParam
        : currentTemplateCodeFilter;

    return templateCode || null;
  }, [getFilterAppliedByKey, getValueQueryParams, getValueCurrentFilters]);

  const parseVehicleIdForRequest = useCallback((): Nullable<string> => {
    const currentTypeVehicleFilterApplied = getFilterAppliedByKey('vehicle');

    const currentVehicleQueryParam = getValueQueryParams('vehicle');
    const currentVehicleFilter = getValueCurrentFilters('vehicle');

    let vehicledId: Nullable<string> = null;

    if (
      currentTypeVehicleFilterApplied === 'queryParams' &&
      currentVehicleQueryParam
    ) {
      vehicledId = currentVehicleQueryParam;
    }

    if (currentTypeVehicleFilterApplied === 'filters' && currentVehicleFilter) {
      vehicledId = currentVehicleFilter.id;
    }

    return vehicledId;
  }, [getFilterAppliedByKey, getValueCurrentFilters, getValueQueryParams]);

  const parseDriverIdForRequest = useCallback((): Nullable<string> => {
    const currentTypeDriverFilterApplied = getFilterAppliedByKey('driver');

    const currentDriverQueryParam = getValueQueryParams('driver');
    const currentDriverFilter = getValueCurrentFilters('driver');

    let driverId: Nullable<string> = null;

    if (
      currentTypeDriverFilterApplied === 'queryParams' &&
      currentDriverQueryParam
    ) {
      driverId = currentDriverQueryParam;
    }

    if (currentTypeDriverFilterApplied === 'filters' && currentDriverFilter) {
      driverId = currentDriverFilter.id;
    }

    return driverId;
  }, [getFilterAppliedByKey, getValueCurrentFilters, getValueQueryParams]);

  const parseFiltersRequest = useCallback(
    (): TemplateRouteFilter => ({
      templateCode: parseTemplateCodeForRequest(),
      vehicleId: parseVehicleIdForRequest(),
      driverId: parseDriverIdForRequest(),
    }),
    [
      parseTemplateCodeForRequest,
      parseVehicleIdForRequest,
      parseDriverIdForRequest,
    ],
  );

  return {
    filters,
    setFilterByKey: setFilterAppliedByKey,
    handleChangeInputFilter: handleChangeInputFilterApplied,
    clearFilters: clearFiltersApplied,
    parseFiltersRequest,
  };
};
